import { apiRequest } from '@/lib/api/client';
import type {
  AuthorIntakeItem,
  ClientOrderItem,
  DiscussionMessage,
  IntakeFilterKind,
  OrdersHubOverview,
  PaymentCreatePayload,
  PaymentRecord,
  SharedArticleItem,
  TimelineEvent,
} from './types';

export const ordersPubApi = {
  getOverview: async (): Promise<OrdersHubOverview> => {
    return apiRequest<OrdersHubOverview>('/orders-publications/overview', {
      channel: 'platform',
    });
  },

  listIntakes: async (params?: {
    kind?: IntakeFilterKind;
    status?: string;
    search?: string;
  }): Promise<{ items: AuthorIntakeItem[]; stats: OrdersHubOverview['intake_stats'] }> => {
    return apiRequest('/orders-publications/intakes', {
      channel: 'platform',
      query: {
        kind: params?.kind === 'ALL' ? undefined : params?.kind,
        status: params?.status === 'ALL' ? undefined : params?.status,
        search: params?.search,
      },
    });
  },

  getIntake: async (id: string): Promise<AuthorIntakeItem> => {
    return apiRequest(`/orders-publications/intakes/${id}`, {
      channel: 'platform',
    });
  },

  updateIntakeStatus: async (
    id: string,
    payload: { status: string; comment?: string }
  ): Promise<AuthorIntakeItem> => {
    return apiRequest(`/orders-publications/intakes/${id}/status`, {
      method: 'PATCH',
      channel: 'platform',
      body: payload,
    });
  },

  moderateIntake: async (
    id: string,
    payload: { decision: string; comment?: string }
  ): Promise<AuthorIntakeItem> => {
    return apiRequest(`/orders-publications/intakes/${id}/moderate`, {
      method: 'POST',
      channel: 'platform',
      body: payload,
    });
  },

  linkIntake: async (
    id: string,
    params: { orderId?: string; articleId?: string }
  ): Promise<AuthorIntakeItem> => {
    return apiRequest(`/orders-publications/intakes/${id}/link-order`, {
      method: 'POST',
      channel: 'platform',
      query: {
        order_id: params.orderId,
        article_id: params.articleId,
      },
    });
  },

  listOrders: async (params?: {
    status?: string;
    database?: string;
    payment?: string;
    type?: string;
    search?: string;
  }): Promise<{ items: ClientOrderItem[]; stats: OrdersHubOverview['orders_stats'] }> => {
    return apiRequest('/orders-publications/orders', {
      channel: 'platform',
      query: {
        status: params?.status === 'ALL' || params?.status === 'Все' ? undefined : params?.status,
        database: params?.database === 'ALL' || params?.database === 'Все' ? undefined : params?.database,
        payment: params?.payment === 'ALL' || params?.payment === 'Все' ? undefined : params?.payment,
        type: params?.type === 'ALL' || params?.type === 'Все' ? undefined : params?.type,
        search: params?.search,
      },
    });
  },

  getOrderDetail: async (id: string): Promise<ClientOrderItem> => {
    return apiRequest(`/orders-publications/orders/${id}`, {
      channel: 'platform',
    });
  },

  createOrder: async (payload: Partial<ClientOrderItem>): Promise<ClientOrderItem> => {
    return apiRequest('/orders-publications/orders', {
      method: 'POST',
      channel: 'platform',
      body: payload,
    });
  },

  updateOrder: async (
    id: string,
    payload: Partial<ClientOrderItem>
  ): Promise<ClientOrderItem> => {
    return apiRequest(`/orders-publications/orders/${id}`, {
      method: 'PATCH',
      channel: 'platform',
      body: payload,
    });
  },

  addPayment: async (
    orderId: string,
    payload: PaymentCreatePayload
  ): Promise<PaymentRecord> => {
    return apiRequest(`/orders-publications/orders/${orderId}/payments`, {
      method: 'POST',
      channel: 'platform',
      body: payload,
    });
  },

  listArticles: async (params?: {
    stage?: string;
    status?: string;
    search?: string;
  }): Promise<{ items: SharedArticleItem[]; stats: OrdersHubOverview['shared_stats'] }> => {
    return apiRequest('/orders-publications/articles', {
      channel: 'platform',
      query: {
        stage: params?.stage === 'ALL' || params?.stage === 'Все' ? undefined : params?.stage,
        status: params?.status === 'ALL' || params?.status === 'Все' ? undefined : params?.status,
        search: params?.search,
      },
    });
  },

  getArticleDetail: async (id: string): Promise<SharedArticleItem> => {
    return apiRequest(`/orders-publications/articles/${id}`, {
      channel: 'platform',
    });
  },

  createArticle: async (payload: Partial<SharedArticleItem>): Promise<SharedArticleItem> => {
    return apiRequest('/orders-publications/articles', {
      method: 'POST',
      channel: 'platform',
      body: payload,
    });
  },

  updateArticle: async (
    id: string,
    payload: Partial<SharedArticleItem>
  ): Promise<SharedArticleItem> => {
    return apiRequest(`/orders-publications/articles/${id}`, {
      method: 'PATCH',
      channel: 'platform',
      body: payload,
    });
  },

  getTimeline: async (type: string, id: string): Promise<TimelineEvent[]> => {
    return apiRequest(`/orders-publications/timeline/${type}/${id}`, {
      channel: 'platform',
    });
  },

  getDiscussions: async (type: string, id: string): Promise<DiscussionMessage[]> => {
    return apiRequest(`/orders-publications/discussions/${type}/${id}`, {
      channel: 'platform',
    });
  },

  postDiscussion: async (
    type: string,
    id: string,
    payload: { message: string; attachment_name?: string; attachment_url?: string }
  ): Promise<DiscussionMessage> => {
    return apiRequest(`/orders-publications/discussions/${type}/${id}`, {
      method: 'POST',
      channel: 'platform',
      body: payload,
    });
  },
};
